/**
 * attachmentTypes — welke bijlagen mogen er bij een project (veldwerkopdracht: boorplan, KLIC,
 * kaart, offerte, V&G-plan)?
 *
 * BEWUST EEN WITTE LIJST. Alles wat niet genoemd staat, wordt geweigerd: geen .exe, .bat, .js,
 * .html, .svg (kan script bevatten), geen bestanden met macro's (.docm, .xlsm) en geen .zip (kan
 * van alles bevatten). Wie één van die bestanden echt nodig heeft, zet hem in een PDF.
 *
 * Een extensie is makkelijk te veranderen, dus de backend kijkt OOK naar de eerste bytes: een
 * hernoemde .exe ('MZ…') is geen PDF ('%PDF') en geen Word-bestand. Tekstformaten (txt, csv, dxf,
 * kml, eml) hebben geen vaste kop; die moeten tekst zijn (geen nul-bytes) en mogen niet met 'MZ'
 * of een script-/HTML-kop beginnen.
 *
 * Bij het downloaden zet de backend het content-type uit DEZE lijst (niet wat de browser bij het
 * uploaden meestuurde), met Content-Disposition: attachment en nosniff.
 */

type Magic = 'pdf' | 'zip' | 'ole' | 'rtf' | 'jpeg' | 'png' | 'tiff' | 'dwg' | 'text';

interface AttachmentType { mime: string; magic: Magic; label: string }

export const ATTACHMENT_TYPES: Record<string, AttachmentType> = {
  pdf:  { mime: 'application/pdf', magic: 'pdf', label: 'PDF' },
  doc:  { mime: 'application/msword', magic: 'ole', label: 'Word' },
  docx: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', magic: 'zip', label: 'Word' },
  xls:  { mime: 'application/vnd.ms-excel', magic: 'ole', label: 'Excel' },
  xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', magic: 'zip', label: 'Excel' },
  ppt:  { mime: 'application/vnd.ms-powerpoint', magic: 'ole', label: 'PowerPoint' },
  pptx: { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', magic: 'zip', label: 'PowerPoint' },
  odt:  { mime: 'application/vnd.oasis.opendocument.text', magic: 'zip', label: 'OpenDocument' },
  ods:  { mime: 'application/vnd.oasis.opendocument.spreadsheet', magic: 'zip', label: 'OpenDocument' },
  odp:  { mime: 'application/vnd.oasis.opendocument.presentation', magic: 'zip', label: 'OpenDocument' },
  rtf:  { mime: 'application/rtf', magic: 'rtf', label: 'RTF' },
  txt:  { mime: 'text/plain; charset=utf-8', magic: 'text', label: 'tekst' },
  csv:  { mime: 'text/csv; charset=utf-8', magic: 'text', label: 'CSV' },
  jpg:  { mime: 'image/jpeg', magic: 'jpeg', label: 'foto' },
  jpeg: { mime: 'image/jpeg', magic: 'jpeg', label: 'foto' },
  png:  { mime: 'image/png', magic: 'png', label: 'afbeelding' },
  tif:  { mime: 'image/tiff', magic: 'tiff', label: 'afbeelding' },
  tiff: { mime: 'image/tiff', magic: 'tiff', label: 'afbeelding' },
  dwg:  { mime: 'application/acad', magic: 'dwg', label: 'AutoCAD' },
  dxf:  { mime: 'application/dxf', magic: 'text', label: 'AutoCAD' },
  kml:  { mime: 'application/vnd.google-earth.kml+xml', magic: 'text', label: 'KML' },
  kmz:  { mime: 'application/vnd.google-earth.kmz', magic: 'zip', label: 'KMZ' },
  msg:  { mime: 'application/vnd.ms-outlook', magic: 'ole', label: 'Outlook-mail' },
  eml:  { mime: 'message/rfc822', magic: 'text', label: 'e-mail' },
};

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** Voor `<input type="file" accept=…>`. */
export const ATTACHMENT_ACCEPT = Object.keys(ATTACHMENT_TYPES).map((e) => `.${e}`).join(',');

/** Leesbare opsomming voor in de interface. */
export const ATTACHMENT_HINT = 'PDF, Word, Excel, PowerPoint, OpenDocument, RTF, tekst/CSV, foto (JPG/PNG/TIFF), AutoCAD (DWG/DXF), KML/KMZ en e-mail (MSG/EML)';

export function attachmentExtension(filename: unknown): string {
  return ((String(filename ?? '').toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1]) || '';
}

const startsWith = (b: Uint8Array, sig: number[], at = 0) => sig.every((v, i) => b[at + i] === v);
const ascii = (b: Uint8Array, n: number) => String.fromCharCode(...b.subarray(0, n));

function magicMatches(magic: Magic, b: Uint8Array): boolean {
  switch (magic) {
    case 'pdf': return ascii(b, 1024).includes('%PDF-');
    case 'zip': return startsWith(b, [0x50, 0x4b, 0x03, 0x04]);
    case 'ole': return startsWith(b, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);
    case 'rtf': return ascii(b, 5) === '{\\rtf';
    case 'jpeg': return startsWith(b, [0xff, 0xd8, 0xff]);
    case 'png': return startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    case 'tiff': return startsWith(b, [0x49, 0x49, 0x2a, 0x00]) || startsWith(b, [0x4d, 0x4d, 0x00, 0x2a]);
    case 'dwg': return ascii(b, 4) === 'AC10';
    case 'text': {
      const head = b.subarray(0, Math.min(b.length, 8192));
      if (head.includes(0)) return false;                       // binair
      if (startsWith(b, [0x4d, 0x5a])) return false;             // 'MZ' = Windows-programma
      const t = new TextDecoder().decode(head).trimStart().slice(0, 64).toLowerCase();
      return !/^(<!doctype html|<html|<script|<svg|#!)/.test(t);
    }
    default: return false;
  }
}

export interface AttachmentCheck { ok: boolean; ext: string; mime: string | null; error: string | null }

/**
 * Mag dit bestand als bijlage? `head` = de eerste bytes (minstens 1 kB; het hele bestand mag ook).
 * Zonder `head` alleen de extensie (voor de browser, vóór het uploaden).
 */
export function checkAttachment(filename: unknown, size: number, head?: Uint8Array): AttachmentCheck {
  const ext = attachmentExtension(filename);
  const type = ATTACHMENT_TYPES[ext];
  const no = (error: string): AttachmentCheck => ({ ok: false, ext, mime: type?.mime ?? null, error });
  if (!type) return no(`Bestanden van het type ${ext ? `.${ext}` : 'zonder extensie'} zijn niet toegestaan. Toegestaan: ${ATTACHMENT_HINT}.`);
  if (!size) return no('Het bestand is leeg.');
  if (size > MAX_ATTACHMENT_BYTES) return no('Het bestand is groter dan 25 MB.');
  if (head && !magicMatches(type.magic, head)) return no(`Dit bestand is geen echt ${type.label}-bestand (.${ext}); de inhoud past niet bij de extensie.`);
  return { ok: true, ext, mime: type.mime, error: null };
}
